import L from 'leaflet';
import { format, subDays } from 'date-fns';
import type { DailyPriceResponse, RegionPriceResponse } from '@/lib/api/data-contracts';
import type { LocatedStation, PetroleumStation } from '@/lib/fuel-station-api';
import type { FuelType } from '@/constants/fuel-colors';

/** 가격 램프 (저렴 → 비쌈): 초록 → 노랑 → 빨강 */
export const PRICE_RAMP = ['#15803d', '#65a30d', '#facc15', '#f97316', '#dc2626'];

const PRICE_FIELD: Record<FuelType, keyof PetroleumStation> = {
  gas: 'priceGasoline',
  diesel: 'priceDiesel',
  premiumGas: 'pricePremiumGas',
  kerosene: 'priceKerosene',
  lpg: 'priceLpg',
};

/** 해당 유종 가격 (0·미보유는 undefined) */
export function getPrice(station: PetroleumStation, fuel: FuelType): number | undefined {
  const v = station[PRICE_FIELD[fuel]];
  return typeof v === 'number' && v > 0 ? v : undefined;
}

/**
 * 주변(반경 radiusKm, 이웃 minNeighbors 이상) 평균 대비 threshold 원 이상 차이 나는 주유소.
 * 값은 "내 가격 − 주변 평균" (음수 = 저렴).
 */
// 격자 버킷팅 — 반경 크기의 셀에 나눠 담고 이웃 3×3 셀만 비교한다.
// 전국 스냅샷(1.1만 건) 전수 비교는 O(n²)=1.1억 회로 수십 초가 걸려 불가.
export function findOutliers(
  stations: PetroleumStation[],
  fuel: FuelType,
  radiusKm = 3,
  minNeighbors = 3,
  threshold = 50
): Map<string, number> {
  // hasLocation 을 안 쓰는 이유: 그 모듈은 api-client 를 끌고 와 이 파일이 순수 유틸이 아니게 됨
  const priced = stations.filter(
    (s): s is LocatedStation => s.latitude !== null && s.longitude !== null && getPrice(s, fuel) !== undefined
  );
  // 셀 한 변 ≥ radiusKm 이 되도록 위경도 각도로 환산 (경도는 한반도 위도 기준)
  const latCell = radiusKm / 111;
  const lngCell = radiusKm / 88;
  const grid = new Map<string, LocatedStation[]>();
  for (const s of priced) {
    const key = `${Math.floor(s.latitude / latCell)},${Math.floor(s.longitude / lngCell)}`;
    const cell = grid.get(key) ?? [];
    cell.push(s);
    grid.set(key, cell);
  }
  const result = new Map<string, number>();
  for (const s of priced) {
    let sum = 0;
    let n = 0;
    const here = L.latLng(s.latitude, s.longitude);
    const row = Math.floor(s.latitude / latCell);
    const col = Math.floor(s.longitude / lngCell);
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        for (const o of grid.get(`${row + dr},${col + dc}`) ?? []) {
          if (o.id === s.id) continue;
          if (here.distanceTo([o.latitude, o.longitude]) <= radiusKm * 1000) {
            sum += getPrice(o, fuel)!;
            n++;
          }
        }
      }
    }
    if (n < minNeighbors) continue;
    const diff = Math.round(getPrice(s, fuel)! - sum / n);
    if (Math.abs(diff) >= threshold) result.set(s.id, diff);
  }
  return result;
}

/** 일별 이력을 주유소별로 날짜 오름차순 정렬해 묶는다 */
export function groupDailyPrices(rows: DailyPriceResponse[]): Map<string, DailyPriceResponse[]> {
  const map = new Map<string, DailyPriceResponse[]>();
  for (const r of rows) {
    if (!r.stationId) continue;
    const list = map.get(r.stationId) ?? [];
    list.push(r);
    map.set(r.stationId, list);
  }
  map.forEach(list => list.sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '')));
  return map;
}

/** 기준일(yyyy-MM-dd) 이하 가장 최근 기록으로 가격을 바꿔 끼운다. 기록이 없으면 가격 없음 */
export function applyDailyPrices(
  stations: PetroleumStation[],
  histories: Map<string, DailyPriceResponse[]>,
  date: string
): PetroleumStation[] {
  return stations.map(s => {
    const list = histories.get(s.id);
    let rec: DailyPriceResponse | undefined;
    if (list) for (const r of list) { if ((r.date ?? '') <= date) rec = r; else break; }
    return {
      ...s,
      priceGasoline: rec?.gas,
      priceDiesel: rec?.diesel,
      pricePremiumGas: rec?.premiumGas,
      priceKerosene: rec?.kerosene,
      priceLpg: rec?.lpg,
      priceUpdatedAt: rec ? `${rec.date}T00:00:00` : undefined,
    };
  });
}

/** 최근 n일 날짜 배열 (오래된 → 오늘), yyyy-MM-dd */
export function recentDates(days: number): string[] {
  return Array.from({ length: days }, (_, i) => format(subDays(new Date(), days - 1 - i), 'yyyy-MM-dd'));
}

export type HeatLevel = 'sido' | 'sigungu';

const AVG_FIELD: Record<FuelType, keyof RegionPriceResponse> = {
  gas: 'avgGas', diesel: 'avgDiesel', premiumGas: 'avgPremiumGas', kerosene: 'avgKerosene', lpg: 'avgLpg',
};

/** 지역 키 → 평균가. sido 레벨은 주유소 수 가중 평균 */
export function regionAverages(rows: RegionPriceResponse[], fuel: FuelType, level: HeatLevel): Map<string, number> {
  const field = AVG_FIELD[fuel];
  const sum = new Map<string, { total: number; n: number }>();
  for (const r of rows) {
    const avg = r[field];
    const n = r.stationCount ?? 0;
    if (typeof avg !== 'number' || !r.sido || n === 0) continue;
    const key = level === 'sido' ? r.sido : `${r.sido}|${r.sigungu ?? ''}`;
    const acc = sum.get(key) ?? { total: 0, n: 0 };
    acc.total += avg * n;
    acc.n += n;
    sum.set(key, acc);
  }
  const out = new Map<string, number>();
  sum.forEach((v, k) => out.set(k, Math.round(v.total / v.n)));
  return out;
}
